import { useState } from 'react';
import Image from 'next/image';
import styled from 'styled-components';
import { useMediaQuery } from '../../../hooks/mediaQuery';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Autoplay } from 'swiper';
import 'swiper/css';
import 'swiper/css/effect-fade';

// Initialize Swiper modules
SwiperCore.use([Autoplay]);

// Define proper types for the info data
interface LandingPageInfoItem {
  header: string;
  paragraph: string;
  key: string;
  imageUrl: string;
}

const LANDING_PAGE_INFO: LandingPageInfoItem[] = [
  {
    header: 'Small-Group Tours',
    paragraph: `Small-groups create connections and allow us to be flexible and go places where large groups don't. No crowded buses, no microphones.`,
    key: 'LPI0',
    imageUrl:
      'https://res.cloudinary.com/see-sight-tours/image/upload/v1678811435/strapi/2_Guide_and_Guests_e794450981.jpg'
  },
  {
    header: 'Local Experiences',
    paragraph:
      'We believe in supporting the communities we live and operate in. By hiring locally, we create jobs to support the local economy and ensure guests receive an authentic experience.',
    key: 'LPI1',
    imageUrl:
      'https://res.cloudinary.com/see-sight-tours/image/upload/v1684849108/size_test_xiqe9l.jpg'
  },
  {
    header: 'Friendly Guides',
    paragraph:
      'Our guides aim to create personal connections with each guest we serve. We truly care about delivering the best customer experience possible.',
    key: 'LPI2',
    imageUrl:
      'https://res.cloudinary.com/see-sight-tours/image/upload/v1689119082/strapi/BSAT_2_4f95fe86f5.jpg'
  }
];

interface StyleProps {
  isActive: boolean;
}

const ButtonStyle = styled.button<StyleProps>`
  transform: ${props => (props.isActive ? 'scale(1.4)' : 'scale(1)')};
  transition: transform 0.3s linear;
`;

// Separate components for better organization
const InfoButton: React.FC<{
  number: number;
  isActive: boolean;
  onClick: () => void;
}> = ({ number, isActive, onClick }) => (
  <ButtonStyle
    isActive={isActive}
    onClick={onClick}
    className="text-sm lg:text-base font-[500] text-[#9B9B9B] px-3 py-2"
  >
    {number.toString().padStart(2, '0')}
  </ButtonStyle>
);

const ImageDisplay: React.FC<{ imageUrl: string }> = ({ imageUrl }) => (
  <Image
    src={imageUrl}
    width={833}
    height={637}
    alt="Tour Experience"
    className="object-cover rounded-lg h-full"
  />
);

const LandingPageInfo: React.FC = () => {
  const [infoNum, setInfoNum] = useState<number>(1);
  const isMobile = useMediaQuery(767);

  const currentImage = LANDING_PAGE_INFO[infoNum - 1]?.imageUrl;

  return (
    <section
      id="landing_page_info"
      className="py-12 w-[90%] lg:w-[85%] 3xl:w-[70%] max-w-[1750px] mx-auto"
    >
      <h2 className="text-[#131313] text-lg lg:text-3xl lg:mb-4 font-semibold pb-6">
        Why Us
      </h2>
      <div className="md:flex">
        {!isMobile ? (
          <Swiper
            className="relative w-full md:hidden sm:flex xsm:flex lg:flex"
            spaceBetween={10}
            slidesPerView={1}
            loop
            effect="slide"
            grabCursor
            a11y={{ enabled: true }}
            observer
            observeParents
            observeSlideChildren
            autoplay={{ delay: 3000, disableOnInteraction: false }}
          >
            {LANDING_PAGE_INFO.map((info, index) => (
              <SwiperSlide key={info.key} className="w-2/6 min-h-full">
                <div className="relative md:hidden sm:flex xsm:flex lg:flex w-[100%] h-[320px] lg:h-[inherit] md:my-auto lg:m-0">
                  <ImageDisplay imageUrl={info.imageUrl} />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <div className="relative md:hidden sm:flex xsm:flex lg:flex w-[100%] h-[320px] lg:h-[inherit] md:my-auto lg:m-0">
            <ImageDisplay imageUrl={currentImage} />
          </div>
        )}

        {isMobile ? (
          <>
            <div className="text-center xsm:mt-6 sm:mt-5 sm:px-12">
              {LANDING_PAGE_INFO.map(
                (info, index) =>
                  index + 1 === infoNum && (
                    <div key={info.key} className="flex">
                      <div>
                        <h2 className="text-[18px] font-[600] text-[#333333]">
                          {info.header}
                        </h2>
                        <p className="text-[#828282] text-[12px] font-[500] leading-[30px]">
                          {info.paragraph.toUpperCase()}
                        </p>
                      </div>
                    </div>
                  )
              )}
            </div>
            <div className="w-[40%] ml-auto mt-5 flex justify-between mr-5">
              {[1, 2, 3].map(num => (
                <InfoButton
                  key={num}
                  number={num}
                  isActive={infoNum === num}
                  onClick={() => setInfoNum(num)}
                />
              ))}
            </div>
          </>
        ) : (
          <div className=" md:max-h-[500px]">
            <div className="flex justify-between flex-col pl-5 h-[100%] lg:w-[500px] xl:w-full">
              {LANDING_PAGE_INFO.map(
                (infoData: LandingPageInfoItem, index: number) => {
                  return (
                    <div key={infoData.key} className="flex pb-6 h-[180px]">
                      <div className="flex w-[40%] lg:hidden mr-[20px]">
                        <Image
                          src={infoData.imageUrl}
                          width={833}
                          height={637}
                          alt="Hornblower Niagara Cruises"
                          className="object-cover rounded-lg h-full"
                        />
                      </div>

                      <div>
                        <h2 className="text-lg lg:text-3xl font-[500] text-[#9B9B9B]">
                          0{index + 1}
                        </h2>
                      </div>

                      <div className="md:pl-6 lg:pl-8 xl:pl-14 lg:w-full md:w-[60%]">
                        <h2 className="text-base lg:text-2xl font-[600] text-[#333333]">
                          {infoData.header}
                        </h2>
                        <p className="text-[#828282] md:text-[14px] lg:text-[14px] xl:text-[16px] md:pt-2 lg:pt-5 font-[500]">
                          {infoData.paragraph.toLocaleUpperCase()}
                        </p>
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default LandingPageInfo;
